import { useState, useEffect, useCallback, useRef, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobalContext } from '~/context/GlobalContext';
import { useAuth } from '~/hooks/useAuth';
import { apiService, apiUpload } from '~/services/apiService';
import { IAvatarItem, ISession } from '~/types/common';
import adminStyles from '~/styles/AdminPanel/AdminPanel.module.css';
import styles from '~/styles/Profile/Profile.module.css';

type UserGender = 'male' | 'female';

interface IProfile {
  id: string;
  nick: string;
  email: string;
  role: 'administrator' | 'moderator' | 'user';
  gender: UserGender;
  avatars: IAvatarItem[];
  avatarIndex: number | null;
  avatarUrl: string | null;
  isBanned?: boolean;
}

interface IFormState {
  nick: string;
  email: string;
  gender: UserGender;
  currentPassword: string;
  newPassword: string;
  newPasswordRepeat: string;
}

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
const MAX_UPLOAD_BYTES = 20 * 1024 * 1024; // 20 MB
const MAX_AVATARS = 3;

function roleLabel(role: string): string {
  if (role === 'administrator') return 'Administrator';
  if (role === 'moderator') return 'Moderator';
  return 'Użytkownik';
}

function Profile() {
  const { context, setContext } = useGlobalContext();
  const { joinRoom, logout } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const successTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [profile, setProfile] = useState<IProfile | null>(null);
  const [form, setForm] = useState<IFormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [busyAvatarId, setBusyAvatarId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [sessions, setSessions] = useState<ISession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [busySessionId, setBusySessionId] = useState<string | null>(null);

  const flashSuccess = useCallback((msg: string) => {
    setSuccess(msg);
    if (successTimer.current) clearTimeout(successTimer.current);
    successTimer.current = setTimeout(() => setSuccess(null), 3500);
  }, []);

  useEffect(() => () => {
    if (successTimer.current) clearTimeout(successTimer.current);
  }, []);

  const syncAuthContext = useCallback((p: IProfile) => {
    setContext({
      authUser: {
        ...context.authUser!,
        nick: p.nick,
        email: p.email,
        gender: p.gender,
        avatarUrl: p.avatarUrl,
        avatars: p.avatars,
        avatarIndex: p.avatarIndex,
      },
    });
  }, [context.authUser, setContext]);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data: IProfile = await apiService('/profile');
      setProfile(data);
      setForm({
        nick: data.nick,
        email: data.email,
        gender: data.gender,
        currentPassword: '',
        newPassword: '',
        newPasswordRepeat: '',
      });
    } catch (e: any) {
      setError(e?.message || 'Nie udało się pobrać profilu.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const data: ISession[] = await apiService('/auth/sessions', 'GET');
      setSessions(Array.isArray(data) ? data : []);
    } catch {
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  const revokeSession = useCallback(async (session: ISession) => {
    // Wylogowanie bieżącego urządzenia = pełne wylogowanie z aplikacji.
    if (session.current) {
      await logout();
      return;
    }
    if (!window.confirm('Wylogować to urządzenie? Zostanie natychmiast rozłączone.')) return;
    setBusySessionId(session.id);
    setError(null);
    try {
      await apiService(`/auth/sessions/${encodeURIComponent(session.id)}`, 'DELETE');
      flashSuccess('Urządzenie zostało wylogowane.');
      await loadSessions();
    } catch (e: any) {
      setError(e?.message || 'Nie udało się wylogować urządzenia.');
    } finally {
      setBusySessionId(null);
    }
  }, [logout, loadSessions, flashSuccess]);

  useEffect(() => {
    if (context.authUser) {
      loadProfile();
      loadSessions();
    } else {
      setLoading(false);
    }
  }, [context.authUser, loadProfile, loadSessions]);

  const goBackToChat = async () => {
    if (context.authUser) {
      const joined = await joinRoom(context.authUser);
      if (joined) return;
    }
    navigate('/');
  };

  const onAvatarFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Dozwolone formaty: PNG, JPG, WEBP, GIF.');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setError('Plik jest zbyt duży (maks. 5 MB).');
      return;
    }
    setError(null);
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const updated: IProfile = await apiUpload('/profile/avatars', fd);
      setProfile(updated);
      syncAuthContext(updated);
      flashSuccess('Dodano zdjęcie.');
    } catch (err: any) {
      setError(err?.message || 'Nie udało się wgrać zdjęcia.');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const deleteAvatar = async (avatarId: string) => {
    if (!profile) return;
    if (!window.confirm('Usunąć to zdjęcie?')) return;
    setBusyAvatarId(avatarId);
    setError(null);
    try {
      const updated: IProfile = await apiService(`/profile/avatars/${encodeURIComponent(avatarId)}`, 'DELETE');
      setProfile(updated);
      syncAuthContext(updated);
      flashSuccess('Usunięto zdjęcie.');
    } catch (e: any) {
      setError(e?.message || 'Nie udało się usunąć zdjęcia.');
    } finally {
      setBusyAvatarId(null);
    }
  };

  const selectAvatar = async (avatarId: string) => {
    if (!profile) return;
    setBusyAvatarId(avatarId);
    setError(null);
    try {
      const updated: IProfile = await apiService('/profile/avatars/select', 'PUT', { avatarId });
      setProfile(updated);
      syncAuthContext(updated);
      flashSuccess('Ustawiono nowe zdjęcie profilowe.');
    } catch (e: any) {
      setError(e?.message || 'Nie udało się zmienić zdjęcia profilowego.');
    } finally {
      setBusyAvatarId(null);
    }
  };

  const submitChanges = async () => {
    if (!form || !profile) return;
    setError(null);

    if (form.newPassword || form.newPasswordRepeat) {
      if (form.newPassword !== form.newPasswordRepeat) {
        setError('Nowe hasła nie są identyczne.');
        return;
      }
      if (form.newPassword.length < 4) {
        setError('Hasło musi mieć co najmniej 4 znaki.');
        return;
      }
      if (!form.currentPassword) {
        setError('Aby zmienić hasło, podaj aktualne hasło.');
        return;
      }
    }

    const payload: Record<string, any> = {};
    if (form.nick.trim() !== profile.nick) payload.nick = form.nick.trim();
    if (form.email.trim().toLowerCase() !== profile.email) payload.email = form.email.trim();
    if (form.gender !== profile.gender) payload.gender = form.gender;
    if (form.newPassword) {
      payload.currentPassword = form.currentPassword;
      payload.newPassword = form.newPassword;
    }

    if (Object.keys(payload).length === 0) {
      flashSuccess('Brak zmian do zapisania.');
      return;
    }

    setSaving(true);
    try {
      const updated: IProfile = await apiService('/profile', 'PUT', payload);
      setProfile(updated);
      setForm({
        nick: updated.nick,
        email: updated.email,
        gender: updated.gender,
        currentPassword: '',
        newPassword: '',
        newPasswordRepeat: '',
      });
      syncAuthContext(updated);
      flashSuccess('Zaktualizowano profil.');
    } catch (e: any) {
      setError(e?.message || 'Nie udało się zaktualizować profilu.');
    } finally {
      setSaving(false);
    }
  };

  const cancelChanges = () => {
    if (!profile) return;
    setForm({
      nick: profile.nick,
      email: profile.email,
      gender: profile.gender,
      currentPassword: '',
      newPassword: '',
      newPasswordRepeat: '',
    });
    setError(null);
  };

  if (!context.authUser) {
    return (
      <div className={adminStyles.accessDenied}>
        <svg width="48" height="48" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
        </svg>
        <h2>Brak dostępu</h2>
        <p>Aby edytować profil, musisz być zalogowany.</p>
        <button className={adminStyles.refreshBtn} onClick={() => navigate('/')}>Wróć na stronę główną</button>
      </div>
    );
  }

  const formChanged =
    !!profile && !!form && (
      form.nick.trim() !== profile.nick ||
      form.email.trim().toLowerCase() !== profile.email ||
      form.gender !== profile.gender ||
      !!form.newPassword
    );

  const slots: (IAvatarItem | null)[] = [...(profile?.avatars || [])];
  while (slots.length < MAX_AVATARS) slots.push(null);

  return (
    <div className={adminStyles.pageWrapper}>
      <div className={adminStyles.topBar}>
        <button className={adminStyles.backBtn} onClick={goBackToChat} title="Wróć do czatu">
          ←
        </button>
        <span className={adminStyles.topBarTitle}>Profil użytkownika</span>
        <button
          className={adminStyles.refreshBtn}
          onClick={loadProfile}
          disabled={loading}
          title="Odśwież dane"
        >
          {loading ? <span className={adminStyles.spinning}>↻</span> : '↻'} Odśwież
        </button>
      </div>

      {error && <div className={adminStyles.errorBar}>{error}</div>}
      {success && <div className={styles.successBar}>{success}</div>}

      {loading || !form || !profile ? (
        <div className={styles.loading}>Ładowanie…</div>
      ) : (
        <div className={styles.content}>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h2>Zdjęcia profilowe</h2>
              <p>Możesz dodać do {MAX_AVATARS} zdjęć. Wybierz, które ma być Twoim awatarem.</p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_TYPES.join(',')}
              onChange={onAvatarFileSelect}
              hidden
            />

            <div className={styles.avatarsGrid}>
              {slots.map((a, idx) => {
                if (!a) {
                  const canAdd = profile.avatars.length < MAX_AVATARS;
                  return (
                    <div
                      key={`empty-${idx}`}
                      className={styles.avatarSlot}
                      onClick={() => {
                        if (!uploadingAvatar && canAdd) fileInputRef.current?.click();
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if ((e.key === 'Enter' || e.key === ' ') && canAdd && !uploadingAvatar) {
                          e.preventDefault();
                          fileInputRef.current?.click();
                        }
                      }}
                    >
                      <div className={styles.slotEmpty}>
                        <span className={styles.slotPlus}>+</span>
                        <span>{uploadingAvatar ? 'Wgrywanie…' : 'Dodaj zdjęcie'}</span>
                      </div>
                    </div>
                  );
                }

                const isSelected = profile.avatarIndex !== null && profile.avatars[profile.avatarIndex]?.id === a.id;
                const isBusy = busyAvatarId === a.id;

                return (
                  <div
                    key={a.id}
                    className={`${styles.avatarSlot} ${styles.avatarSlotFilled} ${isSelected ? styles.avatarSlotSelected : ''}`}
                  >
                    <img className={styles.slotImage} src={a.url} alt={`Zdjęcie ${profile.nick}`} />
                    {isSelected && <span className={styles.slotBadge}>Profilowe</span>}
                    <div className={styles.slotActions}>
                      {!isSelected ? (
                        <button
                          type="button"
                          className={styles.slotSelectBtn}
                          onClick={() => selectAvatar(a.id)}
                          disabled={isBusy}
                        >
                          {isBusy ? '…' : 'Ustaw'}
                        </button>
                      ) : <span />}
                      <button
                        type="button"
                        className={styles.slotDeleteBtn}
                        onClick={() => deleteAvatar(a.id)}
                        disabled={isBusy}
                      >
                        {isBusy ? '…' : 'Usuń'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className={styles.galleryHint}>
              Format: PNG, JPG, WEBP, GIF. Maks. 5 MB. Kliknij na puste pole, aby dodać.
            </p>
          </div>

          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h2>Dane konta</h2>
              <p>Zmień nick, e-mail lub płeć przypisaną do konta.</p>
            </div>
            <div className={styles.formGrid}>
              <label className={styles.field}>
                <span>Rola</span>
                <input
                  type="text"
                  className={`${styles.input} ${styles.inputReadonly}`}
                  value={roleLabel(profile.role)}
                  readOnly
                />
              </label>
              <label className={styles.field}>
                <span>Nick</span>
                <input
                  type="text"
                  className={styles.input}
                  value={form.nick}
                  onChange={e => setForm(s => s && { ...s, nick: e.target.value })}
                  maxLength={32}
                  autoComplete="off"
                />
              </label>
              <label className={styles.field}>
                <span>E-mail</span>
                <input
                  type="email"
                  className={styles.input}
                  value={form.email}
                  onChange={e => setForm(s => s && { ...s, email: e.target.value })}
                  autoComplete="off"
                />
              </label>
              <label className={styles.field}>
                <span>Płeć</span>
                <select
                  className={styles.input}
                  value={form.gender}
                  onChange={e => setForm(s => s && { ...s, gender: e.target.value as UserGender })}
                >
                  <option value="male">Mężczyzna</option>
                  <option value="female">Kobieta</option>
                </select>
              </label>
            </div>
          </div>

          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h2>Zmiana hasła</h2>
              <p>Pozostaw puste, jeśli nie chcesz zmieniać hasła.</p>
            </div>
            <div className={styles.formGrid}>
              <label className={styles.field}>
                <span>Aktualne hasło</span>
                <input
                  type="password"
                  className={styles.input}
                  value={form.currentPassword}
                  onChange={e => setForm(s => s && { ...s, currentPassword: e.target.value })}
                  autoComplete="current-password"
                />
              </label>
              <label className={styles.field}>
                <span>Nowe hasło (min. 6 znaków)</span>
                <input
                  type="password"
                  className={styles.input}
                  value={form.newPassword}
                  onChange={e => setForm(s => s && { ...s, newPassword: e.target.value })}
                  autoComplete="new-password"
                />
              </label>
              <label className={styles.field}>
                <span>Powtórz nowe hasło</span>
                <input
                  type="password"
                  className={styles.input}
                  value={form.newPasswordRepeat}
                  onChange={e => setForm(s => s && { ...s, newPasswordRepeat: e.target.value })}
                  autoComplete="new-password"
                />
              </label>
            </div>
          </div>

          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h2>Zalogowane urządzenia</h2>
              <p>Możesz być zalogowany na maksymalnie 3 urządzeniach. Wyloguj te, których nie rozpoznajesz.</p>
            </div>

            {sessionsLoading ? (
              <div className={styles.loading}>Ładowanie sesji…</div>
            ) : sessions.length === 0 ? (
              <p className={styles.galleryHint}>Brak aktywnych sesji.</p>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {sessions.map(s => (
                  <li
                    key={s.id}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: 12, padding: '12px 14px', borderRadius: 10,
                      border: '1px solid rgba(255,255,255,0.12)', flexWrap: 'wrap',
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 200 }}>
                      {/* <strong>
                        {s.deviceInfo || 'Nieznane urządzenie'}
                        {s.current && <span className={styles.slotBadge} style={{ marginLeft: 8 }}>To urządzenie</span>}
                      </strong> */}
                      <span style={{ opacity: 0.7, fontSize: 13 }}>
                        IP: {s.ipAddress || '—'} · zalogowano: {new Date(s.createdAt).toLocaleString('pl-PL')}
                      </span>
                      <span style={{ opacity: 0.55, fontSize: 12, wordBreak: 'break-all' }}>
                        ID urządzenia: {s.deviceId || '—'}
                      </span>
                      {s.userAgent && (
                        <span style={{ opacity: 0.45, fontSize: 11, wordBreak: 'break-all' }}>
                          User-Agent: {s.userAgent}
                        </span>
                      )}
                      <span style={{ opacity: 0.55, fontSize: 12 }}>
                        ostatnia aktywność: {new Date(s.lastUsedAt).toLocaleString('pl-PL')}
                      </span>
                    </div>
                    <button
                      type="button"
                      className={styles.slotDeleteBtn}
                      onClick={() => revokeSession(s)}
                      disabled={busySessionId === s.id}
                    >
                      {busySessionId === s.id ? '…' : (s.current ? 'Wyloguj' : 'Wyloguj urządzenie')}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className={styles.actionsBar}>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={cancelChanges}
              disabled={!formChanged || saving}
            >
              Anuluj zmiany
            </button>
            <button
              type="button"
              className={styles.saveBtn}
              onClick={submitChanges}
              disabled={!formChanged || saving}
            >
              {saving ? 'Zapisywanie…' : 'Zapisz zmiany'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;
